import { CommonModule } from '@angular/common'
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  EventEmitter,
  inject,
  input,
  Output,
  signal,
  ViewChild,
} from '@angular/core'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { FlexLayoutModule } from '@angular/flex-layout'
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms'
import { MatButtonModule } from '@angular/material/button'
import { MatDialog } from '@angular/material/dialog'
import { MatDividerModule } from '@angular/material/divider'
import { MatFormFieldModule } from '@angular/material/form-field'
import { MatIconModule } from '@angular/material/icon'
import { MatInputModule } from '@angular/material/input'
import { MatProgressBarModule } from '@angular/material/progress-bar'
import { MatSelectModule } from '@angular/material/select'
import {
  Card,
  Clinic,
  ConfirmService,
  EstimateBalance,
  Patient,
} from '@mmx/shared'
import { TranslateModule } from '@ngx-translate/core'
import { take } from 'rxjs/operators'

import { PatientCreditCardComponent } from '../../../payments/patient-credit-card'
import { balanceOutstanding } from '../../balance-utils'
import { resolveMinUpfront } from '../../payment-plan-utils'
import { EstimateBalanceApiService } from '../../services/estimate-balance-api.service'
import { BalancePaymentPlanSummaryComponent } from '../balance-payment-plan-summary/balance-payment-plan-summary.component'
import { NEW_CARD_SENTINEL, SavedCardSelectComponent } from '../saved-card-select/saved-card-select.component'

const MIN_MONTHS = 2
const MAX_MONTHS = 36

export type PaymentPlanState = 'hidden' | 'available' | 'has-plan' | 'disabled' | 'amount-too-low'

@Component({
  selector: 'mmx-balance-payment-plan-panel',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    FlexLayoutModule,
    ReactiveFormsModule,
    MatButtonModule,
    MatDividerModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatProgressBarModule,
    MatSelectModule,
    BalancePaymentPlanSummaryComponent,
    SavedCardSelectComponent,
    TranslateModule,
  ],
  templateUrl: './balance-payment-plan-panel.component.html',
  styleUrls: ['./balance-payment-plan-panel.component.scss'],
})
export class BalancePaymentPlanPanelComponent {
  readonly balance = input<EstimateBalance | null>(null)
  readonly clinic = input<Clinic | null>(null)
  readonly patientId = input<string | null>(null)
  // The PatientCreditCard dialog needs the full Patient model (standardName,
  // address, email) to seed billing details. When omitted, the add-card option
  // is hidden so the picker can't surface an action it can't complete.
  readonly patient = input<Patient | null>(null)

  @Output() readonly planCreated = new EventEmitter<EstimateBalance>()
  @Output() readonly planCanceled = new EventEmitter<EstimateBalance>()

  readonly submitting = signal<boolean>(false)
  readonly error = signal<string | null>(null)

  @ViewChild(SavedCardSelectComponent) savedCardSelect?: SavedCardSelectComponent

  readonly allowNewCard = computed(() => this.patient() != null)

  // Use `inject()` rather than constructor params: the SWC+JIT pipeline used by
  // this lib's spec runner fails to expose signal-input metadata on components
  // whose constructors take DI args, surfacing as NG0303/NG0950 in tests.
  private readonly fb = inject(FormBuilder)
  private readonly api = inject(EstimateBalanceApiService)
  private readonly confirmService = inject(ConfirmService)
  private readonly destroyRef = inject(DestroyRef)
  private readonly dialog = inject(MatDialog)

  // Tracks the saved-card ids known to the picker before the add-card dialog
  // opens so we can identify the newly created card on the post-refresh load
  // and pre-select it without depending on list ordering.
  private knownCardIds = new Set<string>()
  private awaitingNewCard = false

  readonly form = this.fb.group({
    months: this.fb.control<number>(6, [Validators.required, Validators.min(MIN_MONTHS), Validators.max(MAX_MONTHS)]),
    paymentMethodId: this.fb.control<string>('', Validators.required),
    // Seeded from minUpfrontPayment when configured, otherwise monthlyAmount; editable thereafter.
    firstPayment: this.fb.control<number>(0, [Validators.required, Validators.min(0.01)]),
  })

  readonly outstanding = computed(() => {
    const balance = this.balance()
    return balance ? balanceOutstanding(balance) : 0
  })

  // Mirror the months FormControl into a signal so `monthlyAmount` recomputes
  // reactively when the operator changes the term. Reading `form.controls.months.value`
  // inside a `computed()` doesn't establish a Signal dependency on its own.
  private readonly monthsValue = signal<number>(6)

  readonly minLoanAmount = computed(() => this.clinic()?.modulesPreferences?.minLoanAmount ?? 0)

  // Clinic-configured minimum upfront: `$X` flat or `Y%` of outstanding; 0 if unset.
  readonly minUpfrontPayment = computed(() => {
    const raw = this.clinic()?.modulesPreferences?.minPayment
    return resolveMinUpfront(raw, this.outstanding())
  })

  // Global per-clinic cap on plan length. 0 = unset.
  readonly maxLoanTerm = computed(() => Number(this.clinic()?.modulesPreferences?.maxLoanTerm ?? 0))

  /**
   * Min of: per-clinic `maxLoanTerm`, matching `paymentPlanRules` bracket, and `MAX_MONTHS`.
   * Both the dropdown options and the dynamic-cap effect read from here.
   */
  readonly effectiveMaxMonths = computed(() => {
    const candidates: number[] = [MAX_MONTHS]
    const global = this.maxLoanTerm()
    if (global > 0) {
      candidates.push(global)
    }
    const balance = this.balance()
    const rules = this.clinic()?.modulesPreferences?.paymentPlanRules ?? []
    if (balance && rules.length > 0) {
      const outstanding = balanceOutstanding(balance)
      const matching = rules.find((r) => outstanding >= r.minValue && outstanding <= r.maxValue)
      if (matching && matching.maxLoanTerm > 0) {
        candidates.push(matching.maxLoanTerm)
      }
    }
    return Math.min(...candidates)
  })

  readonly monthOptions = computed<number[]>(() => {
    const max = this.effectiveMaxMonths()
    if (max < MIN_MONTHS) {
      return []
    }
    return Array.from({ length: max - MIN_MONTHS + 1 }, (_, i) => MIN_MONTHS + i)
  })

  /** Surfaces a reason when the panel isn't usable so the operator sees an info card, not a blank tab. */
  readonly paymentPlanState = computed<PaymentPlanState>(() => {
    const balance = this.balance()
    const clinic = this.clinic()
    if (!balance || !clinic) {
      return 'hidden'
    }
    if (balance.paymentPlan != null) {
      return 'has-plan'
    }
    if (clinic.modulesPreferences?.paymentPlans !== true) {
      return 'disabled'
    }
    const outstanding = this.outstanding()
    const min = this.minLoanAmount()
    if (outstanding <= 0 || (min > 0 && min >= outstanding)) {
      return 'amount-too-low'
    }
    return 'available'
  })

  /**
   * Per-month amount if installments were equal-sized (no upfront override).
   * Decoupled from `firstPaymentValue` so the seed effect can read it without
   * transitively depending on the form control it writes to — see constructor.
   */
  private readonly monthlyBase = computed(() => {
    const months = this.monthsValue()
    if (!months || months < MIN_MONTHS) {
      return 0
    }
    const outstanding = this.outstanding()
    if (outstanding <= 0) {
      return 0
    }
    return Math.ceil((outstanding / months) * 100) / 100
  })

  /** With a non-zero `firstPayment`, the remainder is split across `months - 1`; otherwise `monthlyBase`. */
  readonly monthlyAmount = computed(() => {
    const firstPayment = this.firstPaymentValue()
    const months = this.monthsValue()
    if (firstPayment > 0 && months > 1) {
      const outstanding = this.outstanding()
      if (outstanding <= 0) {
        return 0
      }
      const remaining = Math.max(0, outstanding - firstPayment)
      return Math.ceil((remaining / (months - 1)) * 100) / 100
    }
    return this.monthlyBase()
  })

  readonly firstPaymentValue = signal<number>(0)

  /** True when first payment ≠ per-month (clinic min, operator override, cents-rounding). Drives split-vs-uniform summary layout. */
  readonly hasDistinctFirstPayment = computed(() => {
    const first = this.firstPaymentValue()
    return first > 0 && first !== this.monthlyAmount()
  })

  /**
   * Number of installments Stripe actually schedules. The upfront is installment
   * #1, charged outside the SubscriptionSchedule (see balance-payment-plan.service.ts).
   * The split-summary displays this count next to the per-month amount so the
   * math reconciles: firstPayment + scheduledInstallmentCount × monthly = outstanding.
   */
  readonly scheduledInstallmentCount = computed(() => {
    const months = this.monthsValue()
    return months > 1 ? months - 1 : 0
  })

  constructor() {
    this.form.controls.months.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((value) => {
        this.monthsValue.set(value ?? 0)
      })

    this.form.controls.firstPayment.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((value) => {
        this.firstPaymentValue.set(typeof value === 'number' && value > 0 ? value : 0)
      })

    // firstPayment tracks the per-month amount (raised to clinic min) as long
    // as the operator hasn't typed an override — so changing the term updates
    // it automatically. Once the control is dirty (operator-typed), we only
    // nudge UP if the value falls below the clinic-configured minimum.
    //
    // The seed reads `monthlyBase` (not `monthlyAmount`) so the effect can't
    // transitively depend on the form control it writes to.
    effect(() => {
      const min = this.minUpfrontPayment()
      const base = this.monthlyBase()
      const ctrl = this.form.controls.firstPayment
      if (ctrl.pristine) {
        const desired = Math.max(base, min)
        if (desired > 0 && Number(ctrl.value ?? 0) !== desired) {
          ctrl.setValue(desired)
          // setValue normally leaves pristine alone, but be explicit so a stray
          // markAsDirty in a directive can't flip the tracking off prematurely.
          ctrl.markAsPristine()
        }
        return
      }
      if (min > 0 && Number(ctrl.value ?? 0) < min) {
        ctrl.setValue(min)
      }
    })

    // Cap months at the effective max (clinic global + matching bracket rule).
    // The dropdown enforces this for fresh selections; this effect is the
    // safety net when balance/clinic swap shrinks the cap below the held value.
    effect(() => {
      const cap = this.effectiveMaxMonths()
      const months = this.form.controls.months.value ?? 0
      if (cap > 0 && months > cap) {
        this.form.controls.months.setValue(cap)
      }
    })

    // "+ Add new card" surfaces as NEW_CARD_SENTINEL. SubscriptionSchedule rejects
    // transient methods, so route through the dialog (saves on customer) and pre-
    // select on refresh. Cancel clears the sentinel so submit isn't blocked by a fake id.
    this.form.controls.paymentMethodId.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((value) => {
        if (value === NEW_CARD_SENTINEL) {
          this.openAddCardDialog()
        }
      })
  }

  /** Parent (collect-payment-card) calls this after adding a new card so the picker reloads. */
  refresh(): void {
    this.savedCardSelect?.refresh()
  }

  onSavedCardsLoaded(cards: Card[]): void {
    if (this.awaitingNewCard) {
      const added = cards.find(card => !this.knownCardIds.has(card.id))
      this.awaitingNewCard = false
      if (added) {
        this.form.controls.paymentMethodId.setValue(added.id)
      } else if (this.form.controls.paymentMethodId.value === NEW_CARD_SENTINEL) {
        // Refresh succeeded but no new card landed — clear the sentinel so the
        // form is in a coherent state for the next selection.
        this.form.controls.paymentMethodId.setValue('')
      }
    }
    this.knownCardIds = new Set(cards.map(card => card.id))
  }

  private openAddCardDialog(): void {
    const patient = this.patient()
    if (!patient) {
      this.form.controls.paymentMethodId.setValue('')
      return
    }
    this.awaitingNewCard = true
    this.dialog
      .open(PatientCreditCardComponent, {
        panelClass: 'dialog-form',
        width: '500px',
        data: { patient },
      })
      .afterClosed()
      .pipe(take(1), takeUntilDestroyed(this.destroyRef))
      .subscribe((saved) => {
        if (saved) {
          this.savedCardSelect?.refresh()
        } else {
          this.awaitingNewCard = false
          this.form.controls.paymentMethodId.setValue('')
        }
      })
  }

  submit(): void {
    const balance = this.balance()
    if (!balance || this.submitting()) {
      return
    }
    if (this.form.invalid) {
      this.form.markAllAsTouched()
      return
    }
    const months = this.form.controls.months.value!
    const monthlyAmount = this.monthlyAmount()
    const firstPayment = Number(this.form.controls.firstPayment.value ?? monthlyAmount)
    const paymentMethodId = this.form.controls.paymentMethodId.value!
    if (!(monthlyAmount > 0)) {
      this.error.set('balance.payment-plan.monthly-amount-invalid')
      return
    }

    // Backend re-checks; surface client-side to avoid the round-trip.
    const minUpfront = this.minUpfrontPayment()
    if (minUpfront > 0 && firstPayment + 0.005 < minUpfront) {
      this.error.set('balance.payment-plan.first-payment-below-min')
      return
    }

    // Re-check the dynamic-cap invariant at submit — a bypass of the effect must not slip through.
    const cap = this.maxLoanTerm()
    if (cap > 0 && months > cap) {
      this.error.set('balance.payment-plan.months-exceeds-max')
      return
    }

    // Surface paymentPlanRules client-side to avoid a 400; backend remains source of truth.
    const ruleViolation = this.checkRuleViolation(balance, months)
    if (ruleViolation) {
      this.error.set(ruleViolation)
      return
    }

    this.submitting.set(true)
    this.error.set(null)
    this.api.createPaymentPlan(balance.id, { months, monthlyAmount, firstPayment, paymentMethodId }).subscribe({
      next: (updated) => {
        this.submitting.set(false)
        this.planCreated.emit(updated)
      },
      error: (err: unknown) => {
        this.submitting.set(false)
        this.error.set(this.extractErrorMessage(err))
      },
    })
  }

  onCancel(): void {
    const balance = this.balance()
    if (!balance || !balance.paymentPlan || this.submitting()) {
      return
    }
    this.confirmService
      .confirm('balance.payment-plan.cancel-confirm')
      .pipe(take(1))
      .subscribe((confirmed) => {
        if (!confirmed) {
          return
        }
        this.submitting.set(true)
        this.error.set(null)
        this.api.cancelPaymentPlan(balance.id).subscribe({
          next: (updated) => {
            this.submitting.set(false)
            this.planCanceled.emit(updated)
          },
          error: (err: unknown) => {
            this.submitting.set(false)
            this.error.set(this.extractErrorMessage(err))
          },
        })
      })
  }

  private checkRuleViolation(balance: EstimateBalance, months: number): string | null {
    const rules = this.clinic()?.modulesPreferences?.paymentPlanRules ?? []
    if (!rules.length) {
      return null
    }
    const outstanding = balanceOutstanding(balance)
    const matching = rules.find((r) => outstanding >= r.minValue && outstanding <= r.maxValue)
    if (matching && months > matching.maxLoanTerm) {
      return 'balance.payment-plan.months-exceeds-rule'
    }
    return null
  }

  private extractErrorMessage(err: unknown): string {
    if (err instanceof Error) {
      return err.message
    }
    if (err && typeof err === 'object') {
      const e = err as { error?: { message?: string }, message?: string }
      return e.error?.message ?? e.message ?? 'balance.payment-plan.error-generic'
    }
    return 'balance.payment-plan.error-generic'
  }
}
